import { useEffect, useState } from "preact/hooks"
import { navigate, useSearch } from "wouter-preact/use-location"
import { ArrowLeft, CalendarPlus, Forward, Upload } from "lucide-react"

import { Button } from "../../../components/ui/button"
import { PromotionCreationWizard } from "./components/promo_creation"
import { CreateCalendarForm } from "./components/calendar_forms"
import { FileUploadArea } from "./components/file_upload"
import { CopyForwardPromotionDialog } from "../promo_calendar/components/copy_forward_promotion_dialog"
import { any_promotions_loading, usePromotionalContextAPI } from "./hooks/use_promotional_context_api"
import { TRADE_PROMOTION_MANAGEMENT_PATH } from "../routes"
import { Promotion } from "./types"

type CreatePageMode = "promotion" | "calendar" | "upload"

/**
 * Dedicated create-promotion page (linked from the sidebar). Unlike the TPM dashboard — where the
 * creation form sits below the calendar and shifts down as promotions hydrate — this page renders
 * only the form, so the layout is stable while data loads in the background.
 */
export function CreatePromotionPage() {
  const {
    calendars,
    promotion_load_by_calendar,
    is_loading_calendars,
    fetchCalendars,
    hydrate_all_calendars,
    handleCreatePromotionWithFiles,
    handleCreateCalendar,
    handleCopyForward,
    handleFileUpload,
    getPromotion,
    isCreatingCalendar,
    isUploading,
  } = usePromotionalContextAPI()

  const [mode, setMode] = useState<CreatePageMode>("promotion")
  const [copyForwardOpen, setCopyForwardOpen] = useState(false)
  const [newCalendarId, setNewCalendarId] = useState<string | null>(null)
  // `?from=<id>` duplicates that promotion: the form starts filled from it (see the modal's Duplicate).
  const search = useSearch()
  const from_id = new URLSearchParams(search).get("from")
  const [sourcePromotion, setSourcePromotion] = useState<Promotion | null>(null)

  useEffect(() => {
    fetchCalendars()
  }, [])

  useEffect(() => {
    if (!from_id) {
      setSourcePromotion(null)
      return
    }
    let cancelled = false
    void getPromotion(from_id).then((promotion) => {
      if (!cancelled) setSourcePromotion(promotion)
    })
    return () => {
      cancelled = true
    }
  }, [from_id])

  // Hydrate every calendar's promotions in the background — powers the existing-customer options,
  // the duplicate warning, and copy-forward search. The form itself renders immediately and never moves.
  useEffect(() => {
    if (calendars.length > 0) hydrate_all_calendars()
  }, [calendars.length])

  function openPromotion(created: Promotion) {
    if (created.id) {
      navigate(`${TRADE_PROMOTION_MANAGEMENT_PATH}?promotion=${created.id}`)
    } else {
      navigate(TRADE_PROMOTION_MANAGEMENT_PATH)
    }
  }

  async function onCreateCalendar(calendarData: any) {
    const createdCalendarId = await handleCreateCalendar(calendarData)
    if (typeof createdCalendarId === "string") {
      setMode("promotion")
      setNewCalendarId(createdCalendarId)
    }
  }

  async function onUploadCalendarFiles(files: File[]) {
    const createdCalendarId = await handleFileUpload(files)
    if (typeof createdCalendarId === "string") {
      setMode("promotion")
      setNewCalendarId(createdCalendarId)
    }
  }

  return (
    <div class="mx-auto w-full max-w-6xl px-4 py-6">
      <a
        href={TRADE_PROMOTION_MANAGEMENT_PATH}
        class="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to promo calendar
      </a>
      <h2 class="mt-2 text-2xl font-semibold text-gray-900">{from_id ? "Duplicate a promotion" : "Create a promotion"}</h2>
      <p class="mt-1 text-sm text-gray-600">
        {from_id
          ? "The form starts filled from the promotion you duplicated. Change the dates, products or terms that differ, then save."
          : "Pick the planning calendar first — customer and year flow from it — then describe the deal. The new promotion opens on the calendar when you're done."}
      </p>

      <div class="mt-6" />

      {mode === "calendar" && (
        <div class="space-y-3">
          <CreateCalendarForm
            onSave={onCreateCalendar}
            onCancel={() => setMode("promotion")}
            isSaving={isCreatingCalendar}
          />
          <p class="text-xs text-gray-500">
            The new calendar will be selected on the promotion form automatically.
          </p>
        </div>
      )}

      {mode === "upload" && (
        <div class="space-y-3">
          <p class="text-sm text-gray-600">
            Upload a promotional calendar file (PDF works best) and the AI Wizard will extract its
            promotions into a new calendar. Review the results before using them for planning or
            deduction validation.
          </p>
          <FileUploadArea onUpload={onUploadCalendarFiles} isUploading={isUploading} />
          <div class="flex justify-start">
            <Button variant="outline" size="sm" onClick={() => setMode("promotion")} disabled={isUploading}>
              Back to promotion form
            </Button>
          </div>
        </div>
      )}

      {/* Hidden, never unmounted, while the calendar or upload panel is open: unmounting threw away
          everything typed, so cancelling "Create a calendar" reset the whole form (Bakhrom, 1st Phorm). */}
      <div class={mode === "promotion" ? undefined : "hidden"}>
        <PromotionCreationWizard
          calendars={calendars}
          calendarsLoading={is_loading_calendars}
          onCreatePromotion={handleCreatePromotionWithFiles}
          onAddNewCalendar={() => setMode("calendar")}
          onCreated={openPromotion}
          autoSelectCalendarId={newCalendarId}
          initialPromotion={sourcePromotion}
        />
      </div>

      {/* The sibling flows sit below the form, not right above the Planning Calendar field, where a
          click aimed at the field could land on "Create a calendar" if the page shifted. */}
      <div class="mt-6 border-t pt-4">
        <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Other ways to add promotions</p>
        <div class="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setCopyForwardOpen(true)}>
            <Forward className="h-4 w-4 mr-2" />
            Copy promotions forward
          </Button>
          <Button
            variant={mode === "calendar" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode(mode === "calendar" ? "promotion" : "calendar")}
          >
            <CalendarPlus className="h-4 w-4 mr-2" />
            Create a calendar
          </Button>
          <Button
            variant={mode === "upload" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode(mode === "upload" ? "promotion" : "upload")}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload calendar with AI
          </Button>
        </div>
      </div>

      <CopyForwardPromotionDialog
        open={copyForwardOpen}
        onOpenChange={setCopyForwardOpen}
        calendars={calendars}
        onCopyForward={handleCopyForward}
        onCopied={(promotions) => promotions[0] && openPromotion(promotions[0])}
        onEnsureAllPromotionsLoaded={hydrate_all_calendars}
        promotionsLoading={any_promotions_loading(is_loading_calendars, promotion_load_by_calendar)}
      />
    </div>
  )
}
